'use client'

import * as Headless from '@headlessui/react'
import React, { useRef, useState, useEffect, cloneElement, isValidElement, useMemo } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { toast } from 'react-hot-toast'

import { useAuth } from '@/context/AuthContext'
import { useTeam } from '@/context/TeamContext'
import { useRole } from '@/context/RoleContext'
import useInboxUnreadCount from '@/hooks/useInboxUnreadCount'
import usePendingAccessRequests from '@/hooks/usePendingAccessRequests'
import useEntitlements from '@/hooks/useEntitlements'
import useHasPaidSeasons from '@/hooks/useHasPaidSeasons'
import useLogout from '@/hooks/useLogout'
import { setAppLanguage } from '@/i18n'

import {
  ShieldCheckIcon,
  HomeIcon,
  TrophyIcon,
  UserGroupIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  LightBulbIcon,
  Cog8ToothIcon,
  QuestionMarkCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  Bars3Icon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  Cog6ToothIcon,
  MegaphoneIcon,
  ChartBarIcon,
  RectangleGroupIcon,
  QueueListIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

import {
  SidebarBody,
  SidebarSection,
  SidebarHeading,
  SidebarLabel,
  SidebarFooter,
  SidebarHeader,
} from './sidebar-elements'

import { Avatar } from '@/components/avatar'
// Keep these for the Desktop Sidebar (SidebarFooter)
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownLabel,
  DropdownItem,
  DropdownDivider,
} from '@/components/Dropdown'

import NotificationDropdown from '@/components/NotificationDropdown';
import LegalLinks from '@/components/LegalLinks';
import { NavbarItem } from './navbar'

// Lime/pitch design tokens — matches Dashboard, AcceptInvite, ParentTeams.
const PALETTE = {
  white: '#FFFFFF',
  pitch: '#0d1f14',       // base bg (matches main app)
  pitchDeep: '#0a1810',   // slightly deeper for separation
  pitchMid: '#162b1e',    // raised cards
  lime: '#b5f03d',        // primary accent
  limeDark: '#94cc28',    // hover/dark state
  border: 'rgba(255,255,255,0.10)',
}

const HEAD_FONT = "'Barlow Condensed', sans-serif"

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(Boolean(mql.matches))
    onChange()
    mql.addEventListener?.('change', onChange)
    return () => mql.removeEventListener?.('change', onChange)
  }, [query])
  return matches
}

function PingDot({ show, pulse = true, tone = 'accent', className = '' }) {
  if (!show) return null
  const color = PALETTE.lime
  return (
    <>
      <span aria-hidden className={clsx('pointer-events-none absolute h-2.5 w-2.5 rounded-full z-50', className)} style={{ backgroundColor: color }} />
      {pulse ? <span aria-hidden className={clsx('pointer-events-none absolute h-2.5 w-2.5 rounded-full opacity-70 z-40 animate-ping', className)} style={{ backgroundColor: color }} /> : null}
    </>
  )
}

function CountPill({ count, tone = 'accent', ping = false, className = '' }) {
  if (!count) return null
  const color = PALETTE.lime
  return (
    <span className={clsx('relative inline-flex items-center justify-center', className)}>
      {ping && <span className="absolute inset-0 rounded-full opacity-80 animate-ping" style={{ backgroundColor: color }} />}
      <span className="relative min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold text-[#0d1f14] grid place-items-center" style={{ backgroundColor: color, fontVariantNumeric: 'tabular-nums', boxShadow: '0 0 0 2px rgba(13,31,20,0.6)' }}>
        {count > 99 ? '99+' : count}
      </span>
    </span>
  )
}

/**
 * Collapsible section that actually shows/hides its content.
 * - Persists open/closed state to localStorage
 * - Forces open when `disableToggle` is true (mobile) or sidebar is `collapsed`
 * - Exposes aria-expanded/aria-controls on the toggle
 */
function CollapsibleSection({
  id,
  title,
  collapsed = false,
  disableToggle = false,
  defaultOpen = true,
  children,
  className,
}) {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    if (disableToggle) { setOpen(true); return }
    try {
      const saved = localStorage.getItem(`scrimly.sidebar.section.${id}`)
      if (saved !== null) setOpen(saved === '1')
    } catch {}
  }, [id, disableToggle])

  const toggle = () => {
    if (disableToggle) return
    const next = !open
    setOpen(next)
    try { localStorage.setItem(`scrimly.sidebar.section.${id}`, next ? '1' : '0') } catch {}
  }

  // When the whole sidebar is collapsed to icon-only, section headers disappear
  // and content is always visible (icons serve as the grouping).
  const forceOpen = collapsed || disableToggle
  const isOpen = forceOpen || open
  const sectionId = `section-${id}`

  return (
    <SidebarSection className={clsx('overflow-visible', className)}>
      {!collapsed && (
        <div className="flex items-center justify-between pr-1">
          <SidebarHeading
            // Toned down from lime/80 → white/45 per the May-2026 audit.
            // Lime was reserved for state (active rail / icon / badges);
            // using it on chrome labels too made the sidebar feel shouty.
            className="!text-[11px] !font-bold !uppercase !tracking-[1.0px] !text-white/45"
            style={{ fontFamily: HEAD_FONT }}
            id={`${sectionId}-label`}
          >{title}</SidebarHeading>
          {!disableToggle && (
            <button
              type="button"
              onClick={toggle}
              aria-expanded={isOpen}
              aria-controls={sectionId}
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${title} section`}
              className="p-1.5 rounded-md text-white/70 hover:text-[#b5f03d] hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60"
            >
              <ChevronUpIcon
                className={clsx('h-4 w-4 transition-transform duration-200', isOpen ? '' : 'rotate-180')}
              />
            </button>
          )}
        </div>
      )}
      <div
        id={sectionId}
        hidden={!isOpen}
        aria-labelledby={!collapsed ? `${sectionId}-label` : undefined}
        className={clsx('overflow-visible', collapsed ? 'mt-1 space-y-2' : 'mt-2 space-y-1.5')}
      >
        {children}
      </div>
    </SidebarSection>
  )
}

function ActiveRail() {
  return <span aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r" style={{ backgroundColor: PALETTE.lime, boxShadow: `0 0 8px ${PALETTE.lime}80` }} />
}

/**
 * Compact EN/ES language pill for the sidebar footer.
 * Always visible on authenticated pages — writes through setAppLanguage
 * so the choice persists to localStorage and Firestore profile sync.
 */
function SidebarLanguageToggle({ collapsed = false }) {
  const { i18n } = useTranslation()
  const current = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'

  if (collapsed) {
    const next = current === 'en' ? 'es' : 'en'
    return (
      <button
        type="button"
        onClick={() => setAppLanguage(next)}
        className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-[10px] font-bold uppercase tracking-widest text-white/85 hover:bg-[#b5f03d]/20 hover:text-[#b5f03d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60"
        title={`Switch to ${next === 'es' ? 'Español' : 'English'}`}
        aria-label={`Switch to ${next === 'es' ? 'Español' : 'English'}`}
      >
        {current === 'en' ? 'EN' : 'ES'}
      </button>
    )
  }

  return (
    <div
      className="mb-3 inline-flex items-center gap-1 rounded-full bg-white/10 p-1 text-[11px] font-semibold"
      role="group"
      aria-label="Language"
    >
      {['en', 'es'].map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setAppLanguage(code)}
          aria-pressed={current === code}
          className={clsx(
            'px-2.5 py-0.5 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60',
            current === code
              ? 'bg-[#b5f03d] text-[#0d1f14] shadow-sm'
              : 'text-white/70 hover:bg-white/10 hover:text-white'
          )}
        >
          {code === 'en' ? 'EN' : 'ES'}
        </button>
      ))}
    </div>
  )
}

export default function Sidebar({
  onItemClick,
  collapsed = false,
  onToggleCollapsed,
  showCollapseToggle = false,
  className,
}) {
  const { t, i18n } = useTranslation()
  const esUi = String(i18n?.language || 'en').toLowerCase().startsWith('es')
  const location = useLocation()
  const { isAuthenticated, logout, userProfile, firebaseUser } = useAuth()
  // currentTeam is still consulted for chat unread scoping (target id
  // for the inbox count). Team switching itself now lives on
  // /dashboard, not in the sidebar. `teams` is consulted to decide
  // whether to render the empty-state CTA in place of the nav body
  // when a brand-new coach has no teams yet.
  const { currentTeam, teams } = useTeam()
  const hasNoTeams = !Array.isArray(teams) || teams.length === 0

  const isMobile = useMediaQuery('(max-width: 767.98px)')
    
  // 1. Get Entitlements (Founder / Pro)
  // isClub gates the CLUB sidebar section (Soft Gate, May 2026 — see
  // docs/pricing-strategy-v2.md). During Phase 1 (PHASE_1_UNLOCK_ALL
  // flag in useEntitlements.js) every authenticated user reads as
  // isClub=true, so the gate is wired but not enforcing yet. When
  // Phase 3 self-serve Stripe flips the flag, free-tier users
  // immediately stop seeing the entry point — no rebuild required.
  const { isPro, isFounder, isClub, phase1UnlockAll } = useEntitlements(firebaseUser?.uid || null)
  // Tier badges are suppressed while everyone is force-unlocked to the
  // top tier — a PRO pill on every free signup is meaningless chrome
  // and sets up a "why did I lose PRO?" moment at Phase 3. FOUNDER is a
  // real JWT-claim badge and stays. (2026-07-24 sidebar audit.)
  const showProBadge = isPro && !phase1UnlockAll
  const hasPaidSeasons = useHasPaidSeasons(firebaseUser?.uid || null)

  const scope = currentTeam ? 'team' : 'user'
  const targetId = currentTeam ? currentTeam.id : (firebaseUser?.uid || null)

  const { count: chatUnreadCount, increased: inboxIncreased } = useInboxUnreadCount(
    targetId,
    { scope }
  )
   
  const showMessagesPing = chatUnreadCount > 0
  const pulseMessages = showMessagesPing && inboxIncreased

  const { pendingRequests } = usePendingAccessRequests(userProfile)
  const accessRequestsCount = pendingRequests.length

  // Per-team role lookup from RoleContext. The "user mode" concept
  // has been retired — there is no global mode anymore. Coach-only
  // sidebar items gate on hasCoachTeams (does this user have any
  // teams they coach?) instead of a parent/coach flag.
  //
  // Phase 2 (May 2026): the sidebar is now strictly UNIVERSAL chrome.
  // All team-scoped items (Roster, Fields, Seasons, Tryouts, AI
  // Messages, the team picker pill) moved onto the team detail page
  // as TeamSubNav tabs. The sidebar keeps only cross-team surfaces:
  // Dashboard (universal team picker), Schedule (cross-team gateway),
  // Club admin, Inbox, Reports, and Resources.
  const { isParent, hasCoachTeams } = useRole()

  const doLogout = useLogout()
  const handleLogout = async () => {
    await doLogout()
    onItemClick?.()
  }

  const gradientStyle = {
    backgroundColor: PALETTE.pitchDeep,
    backgroundImage: `
      radial-gradient(60% 60% at 10% -10%, rgba(181,240,61,0.05) 0%, rgba(181,240,61,0) 60%),
      linear-gradient(180deg, ${PALETTE.pitch} 0%, ${PALETTE.pitchDeep} 100%),
      radial-gradient(820px 220px at 40px calc(100% - 2px), rgba(181,240,61,0.10), rgba(181,240,61,0) 70%)
    `,
    fontFamily: "'Barlow', sans-serif",
  }

  const activeBg = 'rgba(181,240,61,0.12)'
   
  const widthClasses = className && (className.includes('w-') || className.includes('full')) 
    ? '' 
    : (collapsed ? 'w-20' : 'w-64')

  const linkBase = clsx(
    'group relative flex items-center rounded-lg text-white/80 hover:bg-white/[0.06] hover:text-white transition-colors overflow-visible',
    collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2.5 text-[14px] font-medium'
  )

  const linkActive = clsx(
    'group relative flex items-center rounded-lg text-white overflow-visible',
    collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2.5 text-[14px] font-semibold'
  )

  const iconBase = clsx(
    'text-white/60 group-hover:text-white transition-colors',
    collapsed ? 'h-6 w-6' : 'h-5 w-5'
  )
  const iconActive = clsx(collapsed ? 'h-6 w-6' : 'h-5 w-5', 'text-[#b5f03d]')

  const Label = ({ children }) => (
    <span className={clsx('min-w-0 truncate', collapsed ? 'hidden' : 'inline')}>
      <SidebarLabel>{children}</SidebarLabel>
    </span>
  )

  const maybeTitle = (label) => (collapsed ? { title: label } : undefined)

  const renderNavItem = (to, label, Icon, exact, extraMatch = null) => {
    return (
      <NavLink
        key={to}
        to={to}
        end={Boolean(exact)}
        className={({ isActive }) => {
          const active = isActive || (extraMatch ? extraMatch(location.pathname) : false)
          return active ? linkActive : linkBase
        }}
        onClick={onItemClick}
        style={({ isActive }) => {
          const active = isActive || (extraMatch ? extraMatch(location.pathname) : false)
          if (active) {
            return {
              backgroundColor: activeBg,
              boxShadow: `inset 0 0 0 1px rgba(181,240,61,0.25)`,
            }
          }
          return undefined
        }}
        {...maybeTitle(label)}
        aria-label={collapsed ? label : undefined}
      >
        {({ isActive }) => {
          const active = isActive || (extraMatch ? extraMatch(location.pathname) : false)
          return (
            <>
              {active && !collapsed && <ActiveRail />}
              <Icon className={active ? iconActive : iconBase} />
              <Label>{label}</Label>
            </>
          )
        }}
      </NavLink>
    )
  }

  return (
    <div
      className={clsx(
        'flex h-full flex-col text-white relative transition-[width] duration-200',
        widthClasses,
        className
      )}
      style={gradientStyle}
    >
      <SidebarHeader
        className={clsx(
          'relative flex items-center justify-center pt-5 pb-3',
          collapsed ? 'px-2' : 'px-4'
        )}
      >
        <Link
          to="/"
          onClick={onItemClick}
          aria-label="Scrimly home"
          className={clsx('flex items-center justify-center', collapsed ? 'w-12' : 'w-full')}
        >
          {!collapsed ? (
            <span
              className="text-[#b5f03d] leading-none"
              style={{ fontFamily: HEAD_FONT, fontWeight: 900, fontSize: 32, letterSpacing: '-0.5px' }}
            >
              Scrim<span className="text-white">ly</span>
            </span>
          ) : (
            <span
              className="grid h-10 w-10 place-items-center rounded-xl bg-[#b5f03d] text-[#0d1f14] leading-none"
              style={{ fontFamily: HEAD_FONT, fontWeight: 900, fontSize: 22, letterSpacing: '-0.5px' }}
              aria-hidden="true"
            >
              S
            </span>
          )}
        </Link>

        {showCollapseToggle && typeof onToggleCollapsed === 'function' && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className={clsx(
              'absolute top-5 right-1 inline-flex items-center justify-center rounded-lg p-2',
              'text-white/80 hover:text-[#b5f03d] hover:bg-white/10',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60'
            )}
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            title={collapsed ? t('nav.expand') : t('nav.collapse')}
          >
            {collapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5" />
            )}
          </button>
        )}
      </SidebarHeader>

      {/* Notification bell — pinned below the header, visible at all
          desktop/tablet widths. Previously the bell hung off
          SidebarHeader's `actions`, which were `lg:hidden` (a leftover
          pattern assuming a separate desktop top-navbar that AppShell
          never provides) AND got crammed next to the logo on the
          collapsed iPad-portrait rail. Its own row fixes both: centered
          icon when collapsed, left-aligned when expanded. Panel opens
          rightward (align="left") so it doesn't run off the screen edge
          from the left rail. */}
      <div
        className={clsx(
          'shrink-0 flex border-b border-white/5 pb-2',
          collapsed ? 'justify-center px-3' : 'justify-start px-4'
        )}
      >
        <NotificationDropdown align="left" tone="dark" />
      </div>

      <SidebarBody
        className={clsx(
          'flex-1 overflow-y-auto overflow-x-visible',
          collapsed ? 'p-3 space-y-5' : 'p-4 space-y-6'
        )}
        role="navigation"
        aria-label={t('nav.primaryNavigation')}
      >
        {hasNoTeams && !isParent ? (
          // Empty state — coach with zero teams. The standard nav body is
          // mostly useless for them (Inbox/Money have no scope), so we
          // collapse it to a single CTA. Home, Help, What's New, and
          // Sign-out remain reachable via the avatar dropdown / footer
          // and (on mobile) the BottomTabBar.
          <div className="px-4 py-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
              <SparklesIcon className="h-6 w-6 text-[#b5f03d]" />
            </div>
            <p
              className="text-[13px] font-bold uppercase tracking-[1.2px] text-[#b5f03d] mb-1.5"
              style={{ fontFamily: HEAD_FONT }}
            >
              {esUi ? 'Empecemos' : "Let's get started"}
            </p>
            {/* Honest copy (2026-07-24 audit): the old line promised
                weather-watching "tonight" — weather needs a location the
                coach hasn't provided yet. Lead with the real promise. */}
            <p className="text-sm text-white/80 mb-4 leading-relaxed">
              {esUi
                ? 'Crea tu primer equipo — estás a unos minutos de que cada familia reciba tus mensajes en su idioma.'
                : "Create your first team — you're minutes from every family getting your messages in their own language."}
            </p>
            <Link
              to="/teams/create"
              onClick={onItemClick}
              className="inline-flex w-full items-center justify-center min-h-[44px] rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {esUi ? 'Crear mi equipo' : 'Create your team'}
            </Link>
          </div>
        ) : (
          <>
            {/* ── MAIN ─────────────────────────────────────────────
                 Universal entry points. The sidebar is now strictly
                 cross-team chrome — every team-scoped operation
                 (Roster, Fields, Seasons, Tryouts, AI Messages, and
                 also Schedule, since "Schedule" is fundamentally a
                 team-scoped concept) lives on the team detail page's
                 TeamSubNav. /dashboard is the universal team picker
                 for every user.
                 Inbox + Money were promoted out of one-item section
                 wrappers (May 2026) — a section header for a single
                 link reads as CRM clutter; assistant-coach chrome
                 should feel as flat as possible. */}
            <div className={collapsed ? 'space-y-2' : 'space-y-1.5'}>
              {renderNavItem('/dashboard', t('nav.home'), HomeIcon, false)}

              {/* Your teams — direct entry to the team list / roster.
                  Coaches only. Was missing from the desktop sidebar —
                  the roster (a coach's most-used surface) was reachable
                  only via Home → pick team → sub-nav (3+ clicks), while
                  mobile's BottomTabBar already had a Teams tab. Added
                  here for desktop ↔ mobile parity (May-2026 coach-flow
                  audit). UserGroupIcon mirrors the BottomTabBar Teams
                  tab. */}
              {hasCoachTeams && renderNavItem('/teams', t('nav.myTeams'), UserGroupIcon, false)}

              {/* Schedule — cross-team gateway. Routes to /schedule
                  which (via ScheduleGateway) picks the right team's
                  schedule based on context: coach's currentTeam, or
                  the parent's single team, or bounces to /dashboard
                  for picking when ambiguous. Same gateway the
                  BottomTabBar's Schedule tab uses — added here for
                  desktop ↔ mobile parity per the May-2026 audit.
                  QueueListIcon matches BottomTabBar's icon for the
                  same surface so cross-device users get consistent
                  visual treatment. */}
              {renderNavItem('/schedule', t('nav.schedule'), QueueListIcon, false)}

              {/* Messages — cross-team inbox (DMs + every team's
                  announcement channel). Per-team chat lives on
                  TeamSubNav. Promoted to top-level. */}
              <NavLink
                to="/messages"
                className={({ isActive }) => isActive ? linkActive : linkBase}
                onClick={onItemClick}
                style={({ isActive }) => isActive ? { backgroundColor: activeBg, boxShadow: `inset 0 0 0 1px rgba(181,240,61,0.25)` } : undefined}
                {...maybeTitle(t('nav.messages'))}
              >
                {({ isActive }) => (
                  <>
                    {isActive && !collapsed && <ActiveRail />}
                    <span className="relative">
                      <ChatBubbleBottomCenterTextIcon className={isActive ? iconActive : iconBase} />
                      {collapsed && <span className="absolute -top-1 -right-1"><PingDot show={showMessagesPing} pulse={false} tone="accent" className="top-0 left-0" /></span>}
                    </span>
                    {!collapsed && <span className="relative inline-flex items-center overflow-visible"><span className="absolute -left-3.5 top-1/2 -translate-y-1/2"><PingDot show={showMessagesPing} pulse={pulseMessages} tone="accent" /></span><SidebarLabel>{t('nav.messages')}</SidebarLabel></span>}
                    {!collapsed && <div className="ml-auto"><CountPill count={chatUnreadCount} ping={pulseMessages} tone="accent" /></div>}
                  </>
                )}
              </NavLink>

              {/* Access Requests — coach-only conditional sibling.
                  Used to be nested inside the (now-removed) Messaging
                  section; promoted to top-level so it doesn't reappear
                  as a 1-item collapsible. */}
              {hasCoachTeams && accessRequestsCount > 0 && (
                <NavLink to="/coach/access-requests" className={({ isActive }) => isActive ? linkActive : linkBase} onClick={onItemClick} style={({ isActive }) => isActive ? { backgroundColor: activeBg, boxShadow: `inset 0 0 0 1px rgba(181,240,61,0.25)` } : undefined} {...maybeTitle(t('nav.accessRequests'))}>
                  {({ isActive }) => (<>{isActive && !collapsed && <ActiveRail />}<ShieldCheckIcon className={isActive ? iconActive : iconBase} /><Label>{t('nav.accessRequests')}</Label>{!collapsed && <div className="ml-auto"><CountPill count={accessRequestsCount} tone="accent" /></div>}</>)}
                </NavLink>
              )}

              {/* Finances (was "Money", originally "Financial Reports",
                  originally wrapped in a REPORTS section). Coach-only,
                  and since 2026-07-24 only for coaches with at least one
                  PAID season — a free-pilot coach has no money to report
                  on and the tab was calm-page noise. */}
              {hasCoachTeams && hasPaidSeasons && renderNavItem('/reports/financial', t('nav.finances'), ChartBarIcon, false)}
            </div>

            {/* ── CLUB ─── cross-team admin surface, coach-only AND
                 Club-tier-only AND user actually belongs to a club.
                 The userProfile.clubId pre-condition was added May 2026
                 — without it, every solo coach saw 5 useless nav items
                 during Phase 1 (PHASE_1_UNLOCK_ALL forces isClub=true
                 for everyone). When PHASE_1_UNLOCK_ALL flips to false
                 in Phase 3, the existing isClub tier check kicks in on
                 top of this; clubId is just a sensible "do they have a
                 club at all?" gate that should always have been here. */}
            {hasCoachTeams && isClub && !!userProfile?.clubId && (() => {
              const cid = userProfile.clubId;
              const safeCid = encodeURIComponent(cid);
              const clubItems = [
                [`/clubs/${safeCid}`,           t('nav.clubOverview'), BuildingOffice2Icon, true],
                [`/clubs/${safeCid}/programs`,  t('nav.programs'),     RectangleGroupIcon,  false],
                [`/clubs/${safeCid}/broadcast`, t('nav.broadcast'),    MegaphoneIcon,       false],
                [`/clubs/${safeCid}/members`,   t('nav.members'),      UserGroupIcon,       false],
                [`/clubs/${safeCid}/settings`,  t('nav.clubSettings'), Cog6ToothIcon,       false],
              ];

              return (
                <CollapsibleSection id="club" title={t('nav.club')} collapsed={collapsed} disableToggle={isMobile}>
                  {clubItems.map(([to, label, Icon, exact]) => renderNavItem(to, label, Icon, exact))}
                </CollapsibleSection>
              );
            })()}

            {/* Help — flat. Resources section wrapper removed (was a 1-2
                item header per the May-2026 sidebar audit); What's New
                moved to the avatar dropdown since it's a once-per-month
                surface that didn't earn a top-level slot. */}
            <NavLink to="/support" className={({ isActive }) => isActive ? linkActive : linkBase} onClick={onItemClick} style={({ isActive }) => isActive ? { backgroundColor: activeBg, boxShadow: `inset 0 0 0 1px rgba(181,240,61,0.25)` } : undefined} {...maybeTitle(t('nav.helpSupport'))}>
              {({ isActive }) => (<>{isActive && !collapsed && <ActiveRail />}<QuestionMarkCircleIcon className={isActive ? iconActive : iconBase} /><Label>{t('nav.helpSupport')}</Label></>)}
            </NavLink>
          </>
        )}
      </SidebarBody>

      {isAuthenticated && userProfile ? (
        <SidebarFooter
          className={clsx(
            'relative overflow-visible border-t border-white/10',
            collapsed ? 'p-3' : 'p-4'
          )}
        >
          <SidebarLanguageToggle collapsed={collapsed} />

          {/* "Account" heading removed per May-2026 sidebar audit — the
              avatar block below is self-evidently the account section;
              the heading was redundant chrome. */}

          {/* Desktop Dropdown (Still using the wrapper, which is fine for desktop) */}
          <Dropdown>
            <DropdownButton
              as="button"
              className={clsx(
                'flex w-full items-center rounded-lg text-left hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60 group transition-colors',
                collapsed ? 'justify-center p-2' : 'gap-3 p-2'
              )}
              title={collapsed ? t('nav.account') : undefined}
              aria-label={collapsed ? t('nav.account') : undefined}
            >
               
              <div className="relative flex-shrink-0">
                {showProBadge && !collapsed && (
                  <>
                    <span
                      className="absolute -top-2 left-1/2 z-30 -translate-x-1/2 transform rounded-full px-2 py-0.5 text-[10px] font-bold shadow-md text-[#0d1f14]"
                      style={{
                        background: PALETTE.lime,
                        fontFamily: HEAD_FONT,
                        letterSpacing: '0.5px',
                      }}
                    >
                      PRO
                    </span>
                    <span
                      aria-hidden="true"
                      className="absolute -inset-0.5 z-10 rounded-full"
                      style={{
                        background: `linear-gradient(45deg, ${PALETTE.lime}, ${PALETTE.limeDark})`,
                      }}
                    />
                  </>
                )}

                <Avatar
                  src={userProfile.photoURL || firebaseUser?.photoURL || '/default-avatar.png'}
                  className={clsx('relative z-20 flex-shrink-0 rounded-full transition-transform group-hover:scale-105', 'size-10')}
                  alt={userProfile.displayName || 'User'}
                  style={{
                    boxShadow: (showProBadge || isFounder)
                        ? `0 0 0 2px ${PALETTE.pitchDeep}, 0 0 0 4px ${PALETTE.lime}`
                        : 'none',
                  }}
                />
              </div>

              {!collapsed && (
                <div className="min-w-0 flex-1 flex flex-col justify-center">
                  <div className="truncate text-sm font-semibold text-white mb-0.5">
                    {userProfile.displayName || firebaseUser?.displayName || t('nav.account')}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Role pill — always visible so the user can see at a glance
                        whether they're signed in as a parent or coach. Tier
                        badges (Founder/Pro) appear next to it. */}
                    <span
                      className={clsx(
                        'text-[10px] font-bold tracking-[1px] uppercase px-1.5 py-0.5 rounded border',
                        isParent
                          ? 'bg-sky-400/15 text-sky-300 border-sky-400/30'
                          : 'bg-white/[0.06] text-white/70 border-white/15'
                      )}
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      {isParent ? (esUi ? 'Padre' : 'Parent') : (esUi ? 'Entrenador' : 'Coach')}
                    </span>
                    {isFounder ? (
                      // Founder pill — same shape as the PRO pill below for
                      // visual consistency. Pre-cleanup this had a custom
                      // crown-glyph SVG inside an outer div which read as
                      // visual noise next to the cleaner PRO pill (per the
                      // May-2026 sidebar audit).
                      <span
                        className="bg-[#b5f03d]/15 text-[#b5f03d] text-[10px] font-bold px-1.5 py-0.5 rounded border border-[#b5f03d]/30 tracking-[1px] uppercase"
                        style={{ fontFamily: HEAD_FONT }}
                      >
                        FOUNDER
                      </span>
                    ) : showProBadge ? (
                        <span
                          className="bg-[#b5f03d]/15 text-[#b5f03d] text-[10px] font-bold px-1.5 py-0.5 rounded border border-[#b5f03d]/30 tracking-[1px] uppercase"
                          style={{ fontFamily: HEAD_FONT }}
                        >
                          PRO
                        </span>
                    ) : (
                        <span className="truncate text-xs text-white/50 group-hover:text-white/70 transition-colors">
                           {firebaseUser?.email}
                        </span>
                    )}
                  </div>
                </div>
              )}
              {!collapsed && <ChevronUpIcon className="h-5 w-5 text-white/50 group-hover:text-white" />}
            </DropdownButton>

            <DropdownMenu
              anchor="top end"
              className={clsx(
                'z-[80] mb-2 bottom-full',
                collapsed ? 'ml-2 min-w-60' : 'w-full left-0 min-w-0'
              )}
            >
              <DropdownItem href="/settings">
                <Cog8ToothIcon className="size-4" />
                <DropdownLabel>{t('nav.settings')}</DropdownLabel>
              </DropdownItem>
              <DropdownDivider />
              {/* Help removed from this menu (2026-07-24): it duplicated
                  the always-visible Help item in the main nav — the open
                  menu literally rendered beside it. */}
              {/* What's New — moved out of primary nav into the avatar
                  dropdown per the May-2026 sidebar audit. Once-per-month
                  release-notes surface didn't earn a top-level slot. */}
              <DropdownItem href="/changelog">
                <SparklesIcon className="size-4" />
                <DropdownLabel>{t('nav.whatsNew')}</DropdownLabel>
              </DropdownItem>
              {/* Mailto — the /share-feedback route was never built. Until
                  we have a real in-app feedback flow, route to the founder
                  inbox. Pre-fills subject + URL/UA context to make triage
                  easier on Kurt's end. */}
              <DropdownItem
                href={`mailto:hello@getscrimly.com?subject=${encodeURIComponent('Scrimly feedback')}&body=${encodeURIComponent(`\n\n— Sent from ${typeof window !== 'undefined' ? window.location.href : 'Scrimly'}`)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LightBulbIcon className="size-4" />
                <DropdownLabel>{t('nav.shareFeedback')}</DropdownLabel>
              </DropdownItem>
              <DropdownDivider />
              <DropdownItem onClick={handleLogout}>
                <ArrowRightStartOnRectangleIcon className="size-4" />
                <DropdownLabel>{t('nav.signOut')}</DropdownLabel>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>

          {/* Legal — small footer below the avatar dropdown so users can
              always reach Privacy / Terms / SMS Policy. Hidden when the
              sidebar is collapsed (icon-only mode). */}
          {!collapsed && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <LegalLinks variant="subtle" className="!text-white/55" />
            </div>
          )}
        </SidebarFooter>
      ) : (
        <div className="border-t border-white/10 p-4">
          <SidebarLanguageToggle collapsed={collapsed} />
          <Link
            to="/login"
            className="flex items-center justify-center rounded-lg bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-2 text-sm font-bold uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]/60"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <ArrowRightStartOnRectangleIcon className="mr-2 h-4 w-4" />
            {t('nav.login')}
          </Link>
        </div>
      )}
    </div>
  )
}

